/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Hidden, IconButton, Typography2, breakpoints, useId } from '@coursera/cds-core';

const styles = {
  container: css`
    border-radius: var(--cds-border-radius-300);
    background-color: var(--cds-color-emphasis-primary-background-weak);
    padding: var(--cds-spacing-300) var(--cds-spacing-300) var(--cds-spacing-200) var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200);
      border-radius: var(--cds-border-radius-200);
    }
  `,
  header: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  `,
  headerLeft: css`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: row;
    flex-grow: 0;

    ${breakpoints.down('xs')} {
      flex-grow: 1;
    }
  `,
  footer: css`
    display: flex;
    justify-content: flex-start;
  `,
  title: (centerText: boolean) => css`
    flex-grow: 0;
    text-align: start;

    ${breakpoints.down('xs')} {
      flex-grow: 1;
      text-align: ${centerText ? 'center' : 'start'};
    }
  `,
  content: css`
    padding: var(--cds-spacing-150) 0;
  `,
  backButton: css`
    color: var(--cds-color-neutral-primary);
    margin-inline: -8px 0;
    margin-block: -8px 0;
  `,
  dismissButton: css`
    color: var(--cds-color-neutral-primary);
    margin-inline: 4px -8px;
    margin-block: -8px 0;
  `,
  nextButton: css`
    margin-inline: -8px 0;
    margin-block: -8px;
  `,
  supportContent: css`
    margin-inline: var(--cds-spacing-100);
  `,
};

type CtaProps = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

export type Props = {
  /**
   * The title of the onboarding module
   */
  title: string;
  /**
   * The supporting text of the onboarding module displayed beside
   * the title, only visible on sm and above.
   */
  supportContent?: React.ReactNode;
  /**
   * The back action of the onboarding module
   */
  backAction?: CtaProps;
  /**
   * The next action of the onboarding module
   */
  nextAction: CtaProps;
  /**
   * The dismiss action of the onboarding module
   */
  dismissAction: CtaProps;
  /**
   * The children of the onboarding module
   */
  children?: React.ReactNode;
};

/**
 * OnboardingModule is a component that allows you to create an inline onboarding flow for your application.
 *
 * @example
 * <OnboardingModule
 *  title="Select the skills you'd like to develop"
 *  supportContent="These are recommended based on your role(s)"
 *  backAction={{ label: 'Back', onClick: onBackActionClick }}
 *  nextAction={{ label: 'Next', onClick: onNextActionClick, disabled: isNextActionDisabled }}
 *  dismissAction={{ label: 'Dismiss', onClick: onDismissActionClick }}
 * >
 *  <Typography2 component="p" variant="bodySecondary">
 *    This is a child
 *  </Typography2>
 * </OnboardingModule>
 */

export const OnboardingModule = (props: Props) => {
  const { title, supportContent, backAction, nextAction, dismissAction, children } = props;

  const id = useId();

  return (
    <section css={styles.container} aria-live="polite" aria-labelledby={`${id}-title`}>
      <div css={styles.header}>
        <div css={styles.headerLeft}>
          {backAction && (
            <IconButton
              component="button"
              variant="ghost"
              size="small"
              intent="previous"
              onClick={backAction.onClick}
              disabled={backAction.disabled}
              aria-label={backAction.label}
              tooltip={backAction.label}
              css={styles.backButton}
            />
          )}
          <Typography2 component="h3" variant="subtitleMedium" id={`${id}-title`} css={styles.title(!!backAction)}>
            {title}
          </Typography2>
          {supportContent && (
            <Hidden smDown css={styles.supportContent}>
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {supportContent}
              </Typography2>
            </Hidden>
          )}
        </div>
        <IconButton
          component="button"
          variant="ghost"
          size="small"
          intent="close"
          onClick={dismissAction.onClick}
          disabled={dismissAction.disabled}
          aria-label={dismissAction.label}
          tooltip={dismissAction.label}
          css={styles.dismissButton}
        />
      </div>

      <div css={styles.content}>{children}</div>
      <div css={styles.footer}>
        <Button
          component="button"
          variant="ghost"
          size="small"
          onClick={nextAction.onClick}
          disabled={nextAction.disabled}
          css={styles.nextButton}
        >
          {nextAction.label}
        </Button>
      </div>
    </section>
  );
};

export default OnboardingModule;
